import { useEffect, useState } from "react";
import { X, FileDown } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

const CONFIG = {
  photos: {
    contentKey: "service.musee.photos",
    defaults: {
      eyebrow: "Musée numérique",
      title: "Archives photos",
      banner_image: "/media/museum-photos.jpg",
      intro: "Une galerie chronologique de photographies retraçant la vie universitaire de l'UNISIC.",
    },
    empty: "Aucune photo n'a encore été publiée dans cette archive.",
    layout: "gallery",
  },
  publications: {
    contentKey: "service.musee.publications",
    defaults: {
      eyebrow: "Musée numérique",
      title: "Archives publications",
      banner_image: "/media/museum-publications.jpg",
      intro: "Les publications historiques de l'UNISIC.",
    },
    empty: "Aucune publication historique n'a encore été ajoutée.",
    layout: "docs",
  },
  evenements: {
    contentKey: "service.musee.evenements",
    defaults: {
      eyebrow: "Musée numérique",
      title: "Archives Événements",
      banner_image: "/media/museum-events.jpg",
      intro: "Les moments forts qui ont ponctué la vie de l'UNISIC.",
    },
    empty: "Aucun événement archivé pour l'instant.",
    layout: "events",
  },
};

export default function MuseumSection({ section }) {
  const cfg = CONFIG[section];
  const [content, setContent] = useState(cfg.defaults);
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get(`/content/${cfg.contentKey}`).then((r) => setContent({ ...cfg.defaults, ...(r.data || {}) })).catch(() => {});
    api.get(`/museum/${section}`).then((r) => setItems(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  return (
    <div data-testid={`musee-section-${section}`}>
      <section className="relative h-[38vh] min-h-[280px] overflow-hidden">
        <img src={content.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{content.eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight">{content.title}</h1>
          <p className="mt-3 text-white/85 max-w-3xl">{content.intro}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        {items.length === 0 && (
          <div className="text-center py-20 paper-card p-10">
            <p className="text-slate-500 text-sm">{cfg.empty}</p>
          </div>
        )}

        {cfg.layout === "gallery" && (
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {items.map((it) => (
              <button key={it.id} onClick={() => setPreview(it)} className="group relative overflow-hidden rounded-sm aspect-square bg-slate-100" data-testid={`photo-${it.id}`}>
                {it.image_url && (
                  <img src={it.image_url} alt={it.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-left translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                  <div className="text-white text-sm font-semibold truncate">{it.title}</div>
                  {it.description && <div className="text-white/80 text-xs line-clamp-2">{it.description}</div>}
                </div>
              </button>
            ))}
          </div>
        )}

        {cfg.layout === "docs" && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((it) => (
              <div key={it.id} className="paper-card p-6 flex flex-col" data-testid={`doc-${it.id}`}>
                {it.image_url && <img src={it.image_url} alt="" className="w-full h-40 object-cover rounded-sm mb-4" />}
                <h3 className="font-display font-semibold text-unisic-blue">{it.title}</h3>
                {it.description && <p className="mt-2 text-sm text-slate-600 flex-1">{it.description}</p>}
                {it.file_path && (
                  <a href={fileUrl(it.file_path)} target="_blank" rel="noopener noreferrer" className="mt-4 btn-secondary text-sm justify-center">
                    <FileDown className="w-4 h-4" /> Consulter
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {cfg.layout === "events" && (
          <div className="grid gap-8 md:grid-cols-2">
            {items.map((it) => (
              <div key={it.id} className="paper-card overflow-hidden" data-testid={`event-${it.id}`}>
                {it.image_url && <img src={it.image_url} alt="" className="w-full h-52 object-cover" />}
                <div className="p-6">
                  {it.event_date && <div className="eyebrow eyebrow-dark">{new Date(it.event_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</div>}
                  <h3 className="mt-1 font-display font-semibold text-unisic-blue text-lg">{it.title}</h3>
                  {it.description && <p className="mt-3 text-sm text-slate-600 whitespace-pre-line">{it.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center px-4 py-8" onClick={() => setPreview(null)}>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setPreview(null)} className="p-2 rounded bg-white/10 text-white hover:bg-white/20"><X className="w-4 h-4" /></button>
            </div>
            <img src={preview.image_url} alt={preview.title} className="w-full max-h-[70vh] object-contain" />
            <div className="mt-4 bg-white p-4 rounded-sm">
              <h3 className="font-display font-semibold text-unisic-blue">{preview.title}</h3>
              {preview.description && <p className="mt-2 text-sm text-slate-600">{preview.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
